// Cognito のカスタム認証（メールのマジックリンク）。amplify を使わず、Cognito の JSON API を直接呼ぶ。
// 流れ: InitiateAuth(CUSTOM_AUTH) → メール → /login/callback?token=&email= → RespondToAuthChallenge(ANSWER=token)
// 別の端末・ブラウザで開いたとき（Session が手元に無い）は、API の /auth/resume に token を預けてから
// InitiateAuth をやり直す（Create トリガーが預けた token をメールを送らずに使う）。
import { getConfig } from './config';
import { cognitoErrorMessage } from './errors';

export const SESSION_KEY = 'alfine.auth-session';
export const MAGIC_KEY = 'alfine.magic-link';

export interface AuthSession {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // ミリ秒
  email: string;
}

/** メール送信後に手元へ残すもの（同じブラウザで開けば Session で答える） */
export interface PendingMagicLink {
  email: string;
  session: string;
  createdAt: number;
}

export class AuthError extends Error {
  readonly userMessage: string;
  constructor(readonly type: string, userMessage?: string) {
    super(userMessage ?? cognitoErrorMessage(type));
    this.userMessage = userMessage ?? cognitoErrorMessage(type);
  }
}

// ---- 純関数（テスト対象） ----

export function cognitoEndpoint(region: string): string {
  return `https://cognito-idp.${region}.amazonaws.com/`;
}

export interface CognitoRequest {
  target: 'InitiateAuth' | 'RespondToAuthChallenge';
  body: Record<string, unknown>;
}

export function initiateCustomAuth(clientId: string, email: string): CognitoRequest {
  return { target: 'InitiateAuth', body: { AuthFlow: 'CUSTOM_AUTH', ClientId: clientId, AuthParameters: { USERNAME: email } } };
}

export function respondCustomChallenge(clientId: string, session: string, email: string, token: string): CognitoRequest {
  return {
    target: 'RespondToAuthChallenge',
    body: { ChallengeName: 'CUSTOM_CHALLENGE', ClientId: clientId, Session: session, ChallengeResponses: { USERNAME: email, ANSWER: token } },
  };
}

export function initiateRefresh(clientId: string, refreshToken: string): CognitoRequest {
  return { target: 'InitiateAuth', body: { AuthFlow: 'REFRESH_TOKEN_AUTH', ClientId: clientId, AuthParameters: { REFRESH_TOKEN: refreshToken } } };
}

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** /login/callback の問い合わせ文字列から token と email を取り出す */
export function parseCallback(search: string): { token: string; email: string } | null {
  const q = new URLSearchParams(search);
  const token = (q.get('token') ?? '').trim();
  const email = normalizeEmail(q.get('email') ?? '');
  if (!token || !email || !email.includes('@')) return null;
  return { token, email };
}

// 手元の Session の有効時間（Cognito のカスタム認証の Session は 3 分〜15 分。長めに見て、切れていれば Cognito が断る）
const PENDING_TTL_MS = 15 * 60 * 1000;

/** callback でどちらの道を通るか: 手元の Session で答えるか、/auth/resume を経由するか */
export function planCallback(email: string, pending: PendingMagicLink | null, now: number): 'respond' | 'resume' {
  if (pending && normalizeEmail(pending.email) === normalizeEmail(email) && pending.session && now - pending.createdAt < PENDING_TTL_MS) return 'respond';
  return 'resume';
}

/** 期限の 60 秒前から切れたものとして扱う */
export function isExpired(s: Pick<AuthSession, 'expiresAt'>, now: number, skewMs = 60_000): boolean {
  return s.expiresAt - skewMs <= now;
}

interface AuthenticationResult {
  IdToken?: string;
  AccessToken?: string;
  RefreshToken?: string;
  ExpiresIn?: number;
}

/** Cognito の AuthenticationResult から保存する形を作る。更新時は RefreshToken が返らないので前のものを使う */
export function sessionFromResult(r: AuthenticationResult | undefined, email: string, now: number, prevRefreshToken?: string): AuthSession | null {
  if (!r?.IdToken || !r.AccessToken) return null;
  const refreshToken = r.RefreshToken ?? prevRefreshToken;
  if (!refreshToken) return null;
  return { idToken: r.IdToken, accessToken: r.AccessToken, refreshToken, expiresAt: now + (r.ExpiresIn ?? 3600) * 1000, email: normalizeEmail(email) };
}

/** ログイン後に戻る先。外部や // で始まる行き先は捨てる */
export function safeNextPath(next: string | null | undefined, fallback = '/dashboard'): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/login')) return fallback;
  return next;
}

export function loginPathFor(currentPath: string): string {
  const next = safeNextPath(currentPath, '');
  return next ? `/login?next=${encodeURIComponent(next)}` : '/login';
}

// ---- 保存 ----

function read<T>(key: string): T | null {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, v: unknown | null): void {
  try {
    if (v === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(v));
  } catch {
    // 保存できない環境（プライベートモードなど）では、その場限りで動く
  }
}

export function loadSession(): AuthSession | null {
  const s = read<AuthSession>(SESSION_KEY);
  return s && s.idToken && s.refreshToken ? s : null;
}

export function clearSession(): void {
  write(SESSION_KEY, null);
  write(MAGIC_KEY, null);
}

// ---- Cognito の呼び出し ----

async function callCognito<T>(req: CognitoRequest): Promise<T> {
  const { region } = getConfig();
  let res: Response;
  try {
    res = await fetch(cognitoEndpoint(region), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-amz-json-1.1', 'X-Amz-Target': `AWSCognitoIdentityProviderService.${req.target}` },
      body: JSON.stringify(req.body),
    });
  } catch {
    throw new AuthError('NETWORK');
  }
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throw new AuthError(String(json.__type ?? 'Unknown'));
  return json as T;
}

interface InitiateResult {
  ChallengeName?: string;
  Session?: string;
  AuthenticationResult?: AuthenticationResult;
}

/** ログインリンクを送る。Session とメールを手元に残す */
export async function startMagicLink(rawEmail: string): Promise<void> {
  const email = normalizeEmail(rawEmail);
  const { cognito } = getConfig();
  const r = await callCognito<InitiateResult>(initiateCustomAuth(cognito.clientId, email));
  if (r.ChallengeName !== 'CUSTOM_CHALLENGE' || !r.Session) throw new AuthError('Unexpected');
  write(MAGIC_KEY, { email, session: r.Session, createdAt: Date.now() } satisfies PendingMagicLink);
}

async function answer(email: string, session: string, token: string): Promise<AuthSession> {
  const { cognito } = getConfig();
  const r = await callCognito<InitiateResult>(respondCustomChallenge(cognito.clientId, session, email, token));
  const s = sessionFromResult(r.AuthenticationResult, email, Date.now());
  // 答えが違うと Cognito は次の挑戦を返す（AuthenticationResult が無い）
  if (!s) throw new AuthError('NotAuthorizedException');
  return s;
}

/** 別のブラウザで開いたとき: API に token を預け、挑戦を起こし直す */
async function resumeElsewhere(email: string, token: string): Promise<string> {
  const { apiUrl, cognito } = getConfig();
  let res: Response;
  try {
    res = await fetch(`${apiUrl}/auth/resume`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, token }) });
  } catch {
    throw new AuthError('NETWORK');
  }
  if (!res.ok) throw new AuthError('NotAuthorizedException');
  const r = await callCognito<InitiateResult>(initiateCustomAuth(cognito.clientId, email));
  if (r.ChallengeName !== 'CUSTOM_CHALLENGE' || !r.Session) throw new AuthError('Unexpected');
  return r.Session;
}

/** callback で呼ぶ。成功すればセッションを保存して返す */
export async function completeMagicLink(token: string, rawEmail: string): Promise<AuthSession> {
  const email = normalizeEmail(rawEmail);
  const pending = read<PendingMagicLink>(MAGIC_KEY);
  let s: AuthSession;
  if (planCallback(email, pending, Date.now()) === 'respond') {
    try {
      s = await answer(email, pending!.session, token);
    } catch (e) {
      // 手元の Session が切れていた場合は、別端末と同じ道でやり直す
      if (!(e instanceof AuthError) || e.type === 'NETWORK') throw e;
      s = await answer(email, await resumeElsewhere(email, token), token);
    }
  } else {
    s = await answer(email, await resumeElsewhere(email, token), token);
  }
  write(SESSION_KEY, s);
  write(MAGIC_KEY, null);
  return s;
}

let refreshing: Promise<AuthSession | null> | null = null;

async function refresh(s: AuthSession): Promise<AuthSession | null> {
  const { cognito } = getConfig();
  try {
    const r = await callCognito<InitiateResult>(initiateRefresh(cognito.clientId, s.refreshToken));
    const next = sessionFromResult(r.AuthenticationResult, s.email, Date.now(), s.refreshToken);
    if (next) write(SESSION_KEY, next);
    return next;
  } catch (e) {
    // 通信の失敗では消さない。拒否されたときだけログインし直す
    if (e instanceof AuthError && e.type !== 'NETWORK') {
      clearSession();
      return null;
    }
    throw e;
  }
}

/** 有効な ID トークンを返す。期限切れなら更新する（同時の更新は 1 回にまとめる）。無ければ null */
export async function getIdToken(): Promise<string | null> {
  const s = loadSession();
  if (!s) return null;
  if (!isExpired(s, Date.now())) return s.idToken;
  refreshing ??= refresh(s).finally(() => {
    refreshing = null;
  });
  const next = await refreshing;
  return next?.idToken ?? null;
}
